import { Component, findComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { ListFilter } from '@swisslife/patterns-base/src/molecules/list-filter/js/list-filter';
import '@swisslife/patterns-base/src/molecules/list-filter/js/list-filter';
import { dispatchCustomEvent, EventNames } from '@swisslife/shared-ui/src/utils/custom-event/js/custom-event';
import addComponentAnimation from '@swisslife/shared-ui/src/utils/animations/js/animations';

export interface IItemData {
	$item: JQuery;
	active: boolean;
	category: string;
	subCategories: string[];
}

enum States {
	FilterVisible = 'state-o-overview-teaser--item-visible',
	FilterFade = 'state-o-overview-teaser--item-fade',
	NoResults = 'state-o-overview-teaser--no-results',
}

enum Selectors {
	FilterButton = '.js-a-button--filter',
	TeaserItems = '.js-o-overview-teaser__overview-item',
	NoResults = '.js-o-overview-teaser__no-results-message',
}

@Component('OverviewTeaser')
export class OverviewTeaser extends GondelJqueryComponent {
	private listFilter: ListFilter;
	private defaultCategory: { itemKey: string; itemName: string } = { itemKey: '', itemName: '' };
	private selectedCategory: { itemKey: string; itemName: string } = { itemKey: '', itemName: '' };
	private selectedSubCategories: string[] = [];
	private $allItems: JQuery;
	private itemData: IItemData[] = [];
	private $noResults: JQuery;
	private controllers: any[] = Array();

	/**
	 * Initial setup, registers the OverviewTeaser internally.
	 */
	public start() {
		this.$noResults = $(Selectors.NoResults, this.$ctx);

		// add list-filter dependencies and register callback
		const listFilterComponents = findComponents<ListFilter>(this._ctx, 'ListFilter');

		if (listFilterComponents.length > 0) {
			this.listFilter = listFilterComponents[0];
			this.listFilter.registerCallback(this.listFilterClickCallback);
		}
	}

	public async sync() {
		this.controllers = await addComponentAnimation(this.$ctx);
		// set and show all teaser items at init
		this.$allItems = this.$ctx.find('.js-o-overview-teaser__overview-item');
		this.$allItems.addClass(States.FilterVisible);
		this.$allItems.outerWidth();
		this.$allItems.addClass(States.FilterFade);

		if (this.listFilter) {
			this.defaultCategory = this.listFilter.getDefaultCategoryKey();
			this.selectedCategory = this.defaultCategory;

			this.gatherItemData(this.$allItems);
		}
	}

	public stop() {
		for (const controller of this.controllers) {
			controller.destroy(true);
		}
		if (this.listFilter) {
			this.listFilter.removeCallback(this.listFilterClickCallback);
		}
	}

	private gatherItemData($allItems: JQuery) {
		$allItems.each((index, item) => {
			const $item = $(item);

			const itemData: IItemData = {
				$item,
				active: true,
				category: $item.data('category-key') || '',
				subCategories: $item.data('subcategory-key') ? $item.data('subcategory-key').split(',') : [],
			};

			this.itemData.push(itemData);
		});
	}

	private updateSelectedCategory(filterItem: { itemKey: string; itemName: string }) {
		this.selectedCategory = filterItem;
		this.selectedSubCategories = [];
	}

	/**
	 * add/remove sub categories from the selected array
	 */
	private updateSelectedSubCategory(filterValue: string, isSelected: boolean): void {
		if (isSelected) {
			this.selectedSubCategories.push(filterValue);
		} else {
			const categoryIndex = this.selectedSubCategories.indexOf(filterValue);
			this.selectedSubCategories.splice(categoryIndex, 1);
		}
	}

	private filterItems() {
		$.each(this.itemData, (index, itemData) => {
			// set item active state to true
			itemData.active = true;

			// check if it's not the default category and the currently selected category does NOT match the item category
			if (
				this.selectedCategory.itemKey !== this.defaultCategory.itemKey &&
				itemData.category !== this.selectedCategory.itemKey
			) {
				itemData.active = false;
				return;
			}

			// check if the currently selected sub categories do NOT match the item sub categories
			if (this.selectedSubCategories.length > 0) {
				const allSelectedSubCategoriesMatch = this.selectedSubCategories.every((selectedSubCategory) => {
					return itemData.subCategories.indexOf(selectedSubCategory) >= 0;
				});
				if (!allSelectedSubCategoriesMatch) {
					itemData.active = false;
					return;
				}
			}
		});
	}

	private updateItemsState() {
		let noResults = true;

		this.$allItems.removeClass(States.FilterVisible);
		this.$allItems.removeClass(States.FilterFade);
		// needed so that animation of elements work, which where visible and stay visible after filtering
		this.$allItems.outerWidth();

		for (const itemData of this.itemData) {
			if (itemData.active) {
				itemData.$item.addClass(States.FilterVisible);
				itemData.$item.outerWidth();
				itemData.$item.addClass(States.FilterFade);
				noResults = false;
			}
		}

		if (noResults) {
			this.$noResults.addClass(States.NoResults);
		} else {
			this.$noResults.removeClass(States.NoResults);
		}
	}

	private listFilterClickCallback = (
		filterType: string,
		filterItem: { itemKey: string; itemName: string },
		isSelected: boolean
	): void => {
		let analyticsData = {};
		let suppressAnalyticsClick = false;
		// call method to update class string and array variables
		if (filterType === 'category') {
			this.updateSelectedCategory(filterItem);
			analyticsData = {
				filterCategory: filterType,
				filterName: this.selectedCategory.itemName.trim(),
				filterID: this.selectedCategory.itemKey,
			};
		} else {
			this.updateSelectedSubCategory(filterItem.itemKey, isSelected);
			const filterNames = this.selectedSubCategories.map((subcategory) => {
				const selector = `[data-subcategory-key='${subcategory}']`;
				return this.$ctx
					.find(`${Selectors.FilterButton}${selector}`)
					.get(0)
					.innerText.trim();
			});
			if (filterNames.length === 0) {
				suppressAnalyticsClick = true;
			}
			analyticsData = {
				filterCategory: filterType,
				filterName: filterNames.join('|'),
				filterID: this.selectedSubCategories.join('|'),
			};
		}

		this.filterItems();
		this.updateItemsState();

		if (suppressAnalyticsClick) {
			return;
		}
		dispatchCustomEvent(this.$ctx, EventNames.FilterClick, analyticsData);
	};
}
